import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Currency, SpStrategyCreateRequest, StrategyStateEnum, StrategyType } from "@front/m19-api-client";
import { Marketplaces, StrategyEx, StrategyGroupEx } from "@front/m19-models";
import { AccountSelectionService, SpStrategyService } from "@front/m19-services";
import {
  IButtonDirective,
  IFormFieldComponent,
  IInputDirective,
  ILabelComponent,
  ISelectMenuImports,
  ITooltipDirective,
  MODAL_DATA,
  ModalOptions,
  ModalRef,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  AlgoModeConfig,
  AlgoModeSelectionComponent,
} from "@m19-board/shared/algo-mode-selection/algo-mode-selection.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import { Observable, switchMap } from "rxjs";
import { FormsModule } from "@angular/forms";
import { SpStrategyEx } from "@front/m19-models/strategy/SpStrategyEx";

export type TargetProductStrategy = {
  type: "NewProductStrategy" | "NewStrategyGroup" | "ExistingStrategy";
  productStrategy?: SpStrategyEx;
  productStrategyToCreate?: SpStrategyCreateRequest;
};

type SelectedProductStrategy = "NewProductStrategy" | "NewStrategy" | StrategyEx;

export interface StrategyGroupMoveProductModalData {
  strategyGroup: StrategyGroupEx;
  source: SpStrategyEx;
  asinsToMove: string[];
}

@UntilDestroy()
@Component({
  templateUrl: "./strategy-group-move-product-modal.component.html",
  imports: [
    TranslocoDirective,
    ISelectMenuImports,
    AlgoModeSelectionComponent,
    IButtonDirective,
    ITooltipDirective,
    IFormFieldComponent,
    ILabelComponent,
    IInputDirective,
    FormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyGroupMoveProductModalComponent implements OnInit {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "md", height: "fill" };

  private translocoService = inject(TranslocoService);
  private strategyService = inject(SpStrategyService);
  private toastrService = inject(ToasterService);
  private accountMarketplaceSelection = inject(AccountSelectionService);

  modalData = inject(MODAL_DATA) as StrategyGroupMoveProductModalData;
  modalRef = inject(ModalRef);

  productStrategy = signal<SelectedProductStrategy>("NewProductStrategy");
  existingStrategies = signal<StrategyEx[]>([]);
  searchQuery = signal("");
  algoModeConfig?: AlgoModeConfig;
  algoModeConfigValid?: boolean;
  productStrategyName?: string;

  filteredExistingStrategies = computed(() => {
    const strategies = this.existingStrategies();
    const query = this.searchQuery().toLowerCase();
    if (!query) return strategies;
    return strategies.filter((s) => (s.name ?? "").toLowerCase().includes(query));
  });

  accountMarketplace = toSignal(this.accountMarketplaceSelection.singleAccountMarketplaceSelection$);
  readonly currencyCode = computed(() => {
    const am = this.accountMarketplace();
    return am ? Marketplaces[am.marketplace].currency : Currency.USD;
  });

  loading = signal(false);

  ngOnInit(): void {
    this.existingStrategies.set(
      this.modalData.strategyGroup.productStrategies.filter((s) => s.strategyId != this.modalData.source.strategyId),
    );
    this.productStrategy.set("NewProductStrategy");
  }

  close() {
    this.modalRef.close();
  }

  setProductStrategy(selected: SelectedProductStrategy | undefined) {
    if (selected == null) return;
    this.productStrategy.set(selected);
  }

  setAlgoModeConfig(algoModeConfig: Partial<AlgoModeConfig>) {
    this.algoModeConfig = algoModeConfig as AlgoModeConfig;
    this.algoModeConfigValid = algoModeConfig.isValid;
  }

  setProductStrategyName(productStrategyName: string) {
    this.productStrategyName = productStrategyName;
  }

  invalid() {
    const selected = this.productStrategy();
    return (
      selected == undefined ||
      (selected == "NewProductStrategy" &&
        (!this.algoModeConfigValid || !this.productStrategyName || this.productStrategyName.length == 0))
    );
  }

  invalidTooltip() {
    const selected = this.productStrategy();
    if (selected == undefined) {
      return "Select a main strategy to continue";
    }
    if (selected == "NewProductStrategy" && !this.algoModeConfigValid) {
      return "Algorithm configuration is invalid";
    }
    if (
      (selected == "NewProductStrategy" || selected == "NewStrategy") &&
      (!this.productStrategyName || this.productStrategyName.length == 0)
    ) {
      return "Enter a strategy name";
    }
    return "";
  }

  save() {
    const target: TargetProductStrategy = this.getStrategyToCreate(this.productStrategy());
    const am = this.accountMarketplace();
    let strategyCall: Observable<any> | undefined;

    this.loading.set(true);
    if (target.type == "NewProductStrategy") {
      strategyCall = this.strategyService
        .createStrategy(<SpStrategyCreateRequest>{
          ...target.productStrategyToCreate,
          asins: this.modalData.asinsToMove,
        })
        .pipe(
          switchMap(() =>
            this.strategyService.deleteAsinsFromStrategy(this.modalData.source, this.modalData.asinsToMove),
          ),
        );
    }

    if (target.type == "NewStrategyGroup") {
      strategyCall = this.strategyService.moveAsinsToNewStrategyGroup(
        am!.accountId,
        am!.marketplace,
        this.modalData.strategyGroup,
        target.productStrategyToCreate!,
      );
    }

    if (target.type == "ExistingStrategy") {
      strategyCall = this.strategyService.moveAsinsToStrategy(
        this.modalData.asinsToMove,
        this.modalData.source,
        target.productStrategy!,
      );
    }

    if (strategyCall) {
      strategyCall.subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("sp-strategy-group-page.asins_moved_to_another_main_strategy"),
          );
          this.modalRef.close(true);
        },
        error: (error: string) => {
          this.toastrService.error(
            this.translocoService.translate("sp-strategy-group-page.error_moving_asins_error", [error]),
            this.translocoService.translate("sp-strategy-group-page.error_moving_asins"),
          );
          this.loading.set(false);
        },
      });
    }
  }

  private getStrategyToCreate(strategyType: SelectedProductStrategy): TargetProductStrategy {
    if (strategyType !== "NewProductStrategy" && strategyType !== "NewStrategy") {
      return {
        type: "ExistingStrategy",
        productStrategy: strategyType as SpStrategyEx,
      };
    }

    const strategy: SpStrategyCreateRequest = {
      state: StrategyStateEnum.ENABLED,
      strategyType: StrategyType.PRODUCT,
      name: this.productStrategyName ?? "",
      ...this.algoModeConfig!,
      acosTarget: this.algoModeConfig!.acosTarget ? this.algoModeConfig!.acosTarget / 100 : undefined,
      asins: this.modalData.asinsToMove,
    };

    if (strategyType == "NewProductStrategy") {
      return {
        type: "NewProductStrategy",
        productStrategyToCreate: { ...strategy, strategyGroupId: this.modalData.strategyGroup.strategyGroupId },
      };
    } else {
      return {
        type: "NewStrategyGroup",
        productStrategyToCreate: strategy,
      };
    }
  }
}
